import { useEffect } from 'react'

import type {
  GetStaticPaths,
  GetStaticProps,
  GetStaticPropsContext,
  GetStaticPropsResult,
  NextPage,
} from 'next'
import { useRouter } from 'next/router'

import type { ArticlePreviewCardProps } from '~/components/atoms/ArticlePreviewCard/ArticlePreviewCard'
import type { BreadcrumbProps } from '~/components/atoms/Breadcrumb/Breadcrumb'
import Breadcrumb from '~/components/atoms/Breadcrumb/Breadcrumb'
import InPageNav from '~/components/blocks/InPageNav/InPageNav'
import RelatedArticles from '~/components/blocks/RelatedArticles/RelatedArticles'
import { resolveBlock } from '~/components/serializers'
import { getTemplate } from '~/lib/pageTemplates'
import {
  createBaseSelector,
  detectBodyModuleTypes,
  getBodyModules,
  myPortfolioBanner,
  notification,
  pageLanguageFilter,
} from '~/schemas/common'
import { getFAQData } from '~/schemas/JsonLd'
import { sanityClientCdn, useSanity } from '~/services/sanity'
import type { SiteSettings } from '~/types/siteSettings'
import {
  buildTranslatedArticlesQuery,
  processInverseQueryResults,
} from '~/utils/articles/translationHelpers'
import { buildBreadcrumb, getBreadcrumbJsonLd } from '~/utils/breadcrumb'
import { getPageBuilderArtistIndexData } from '~/utils/getBodyArtistIndexData'
import { buildTranslatedUrlsQuery } from '~/utils/getHreflangUrls'
import { getInPageNavItemsFromBlocks } from '~/utils/getInPageNavItemsFromBlocks'
import { addTranslations, loadMessages, type Locale } from '~/utils/i18n'
import { supportedLocales } from '~/utils/langPaths'
import { getSEOMetaByType, metaQuery, MetaTemplate } from '~/utils/seo'
import type { PageType } from '~/utils/urlResolver'
import { pageTypes, slugFromContext, urlQuery } from '~/utils/urlResolver'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

import styles from '~/styles/pages.module.css'

export interface PageData {
  title: string
  breadcrumb: BreadcrumbProps['list']
  data: any
  body: Array<{
    _type: string
  }>
  redirectUrl?: string
  breadCrumbHidden: boolean
  relatedArticles: ArticlePreviewCardProps[]
  relatedArticlesTitle?: string
  query: any
  queryParams: any
  preview: boolean
  isEnglishFallback: boolean
  pageTemplate?: string
}

const getQuery = (
  limiter: any,
  scorer: any = '',
  lang: string,
  detectedTypes: string[]
) => {
  return `*[_type == 'page' && slug.current == $slug && ${pageLanguageFilter(limiter)} ${limiter}] ${scorer} [0] {
    ${urlQuery},
    ${buildTranslatedUrlsQuery([...supportedLocales])},
    ${metaQuery('page')},
    redirectUrl,
    title,
    hasGoogleReviews,
    fullGoogleReviews,
    breadCrumbHidden,
    pageTemplate,
    "body": body[] {
      ${getBodyModules(lang, detectedTypes)}
    },
    relatedArticlesTitle,
    ${buildTranslatedArticlesQuery('relatedArticles[]', lang)},
    ${notification},
    ${myPortfolioBanner}
  }`
}

const DynamicPage: NextPage<PageData> = (props: PageData) => {
  const router = useRouter()
  const {
    body = [],
    redirectUrl,
    breadCrumbHidden,
    relatedArticles,
    isEnglishFallback,
    pageTemplate,
  } = props
  const { breadcrumb } = props

  const blocks = body

  // New valuation journey pages (dialog hero) never show a breadcrumb so the
  // hero image sits directly beneath the navigation
  const hasDialogValuationHero = blocks.some(
    (block: any) => block._type === 'valuationHero' && block.useDialogModal
  )
  const hideBreadcrumb = breadCrumbHidden || hasDialogValuationHero

  useEffect(() => {
    if (redirectUrl) router.push(redirectUrl)
  }, [])

  // If this page uses a custom template, render it instead of default
  if (pageTemplate && pageTemplate !== 'default') {
    const template = getTemplate(pageTemplate)
    const TemplateComponent = template.component

    return (
      <>
        <Breadcrumb list={breadcrumb} hidden={hideBreadcrumb} />
        {isEnglishFallback && (
          <div className={styles.notice}>
            This content is currently only available in English
          </div>
        )}
        <TemplateComponent data={props} />
        {relatedArticles && relatedArticles.length > 0 && (
          <RelatedArticles articles={relatedArticles} />
        )}
      </>
    )
  }

  const isValuationsPage = [
    'valuations',
    'valuations-beta',
    'expert-valuation',
    'replacement-valuation',
    'instant-valuation',
  ].includes(props?.queryParams?.slug)
  const displayNavSecond =
    props?.queryParams?.slug === 'sell' ||
    props?.queryParams?.slug?.startsWith('sell-your-art-') ||
    props?.queryParams?.slug === 'the-print-collectors-survey-2025'

  const useOffWhiteInPageNav =
    props?.queryParams?.slug === 'the-print-collectors-survey-2025'

  let navItems = getInPageNavItemsFromBlocks(
    isValuationsPage
      ? blocks.filter((item: any) => item._type !== 'valuationHero')
      : blocks
  )

  if (props?.queryParams?.slug === 'instant-valuation') {
    // remove the first item
    navItems = navItems.slice(1)
  }

  return (
    <>
      <Breadcrumb list={breadcrumb} hidden={hideBreadcrumb} />
      {isEnglishFallback && (
        <div className={styles.notice}>
          This content is currently only available in English
        </div>
      )}
      {!isValuationsPage &&
        !displayNavSecond &&
        navItems &&
        navItems?.length > 0 && (
          <InPageNav
            bordered
            center
            items={navItems}
            title={''}
            idAppend=""
            noMarginTop={breadCrumbHidden}
          />
        )}
      {blocks && Array.isArray(blocks) && (
        <div className={styles.body}>
          {blocks.map((block, i) => {
            if (isValuationsPage && block._type === 'valuationHero') {
              return (
                <>
                  {resolveBlock(block, i)}
                  <InPageNav
                    bordered
                    center
                    items={navItems}
                    title={''}
                    idAppend=""
                    noMarginTop={breadCrumbHidden}
                  />
                </>
              )
            } else if (displayNavSecond && i === 0) {
              return (
                <>
                  {resolveBlock(block, i)}
                  <InPageNav
                    bordered={!useOffWhiteInPageNav}
                    center
                    items={navItems}
                    title={''}
                    idAppend=""
                    offwhite={useOffWhiteInPageNav}
                    noMarginTop={breadCrumbHidden}
                  />
                </>
              )
            } else {
              return resolveBlock(block, i)
            }
          })}
        </div>
      )}

      {relatedArticles && relatedArticles.length > 0 && (
        <RelatedArticles articles={relatedArticles} />
      )}
    </>
  )
}

const getStaticPropsOriginal = async function getStaticProps(
  context: GetStaticPropsContext,
  localizedData: { settings: SiteSettings }
): Promise<GetStaticPropsResult<PageData>> {
  try {
    const pageSlug = slugFromContext(context)
    if (pageSlug === 'insurance') {
      return { notFound: true }
    }
    const { lang } = context.params as { lang: Locale }
    const messages = await loadMessages(lang)

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { client, limiter, scorer } = await useSanity(context)

    // STEP 1: Pre-query to detect which body module types are actually used
    const baseSelector = createBaseSelector(
      'page',
      [`slug.current == $slug`, pageLanguageFilter(limiter)],
      limiter,
      scorer
    )
    const detectedTypes = await detectBodyModuleTypes(
      client,
      baseSelector,
      { slug: pageSlug, lang },
      ['body']
    )

    // STEP 2: Main query with only the required body modules
    const query = getQuery(limiter, scorer, lang, detectedTypes)
    const data = await client.fetch(query, {
      slug: pageSlug,
      lang: lang,
    })

    // If no data found at all, return 404
    if (!data) {
      return {
        notFound: true,
      }
    }

    // Note: Server-side redirect logic for translated slugs has been removed to prevent
    // build-time redirect errors during static generation on Vercel.
    // Client-side redirect (useEffect with redirectUrl) handles runtime redirects instead.

    const pageTypeKeys = Object.keys(pageTypes)
    const urlQueryArr: any = []
    pageTypeKeys.map(key => {
      if (data?.url?.[key] !== null && data?.url?.[key] !== undefined)
        urlQueryArr.push(key)
    })

    const newBody = data.body ? await getPageBuilderArtistIndexData(data) : []
    data.body = newBody

    // Process related articles for non-English languages
    if (
      lang !== 'en' &&
      data.relatedArticles?.length > 0 &&
      data.relatedArticles[0].article
    ) {
      data.relatedArticles = processInverseQueryResults(
        data.relatedArticles,
        lang
      )
    }

    // Fetch template-specific data if a template is specified
    if (data.pageTemplate && data.pageTemplate !== 'default') {
      const template = getTemplate(data.pageTemplate)
      const additionalData = await template.fetchData(client, {
        slug: pageSlug,
        lang,
      })
      // Merge additional data into the base data
      Object.assign(data, additionalData)
    }

    const breadcrumb: BreadcrumbProps['list'] =
      buildBreadcrumb([pageSlug as string], '', undefined, messages, lang) || []
    const breadcrumbJsonLd = breadcrumb ? getBreadcrumbJsonLd(breadcrumb) : []

    // Allow pages without body if they use a custom template (template handles rendering)
    const hasCustomTemplate =
      data.pageTemplate && data.pageTemplate !== 'default'
    if (
      !hasCustomTemplate &&
      data?.body?.length < 1 &&
      urlQueryArr.length < 1
    ) {
      return {
        notFound: true,
      }
    }

    const meta = await getSEOMetaByType(
      MetaTemplate.Page,
      data,
      localizedData.settings as unknown as SiteSettings,
      undefined,
      lang
    )

    return {
      props: {
        notFound: false,
        ...data,
        meta,
        query,
        breadcrumb,
        breadcrumbJsonLd,
        queryParams: { slug: pageSlug },
        preview: !!context.preview,
        noAnimatedTheme: true,
        navTransparent: pageSlug === 'my-portfolio',
        jsonLd: {
          faqJsonLd: getFAQData(data.body),
        },
        // Add a flag to indicate this is fallback English content
        isEnglishFallback: lang !== 'en' && data.language === 'en',
        pageTemplate: data.pageTemplate,
      },
    }
  } catch (error) {
    console.error('getStaticProps error for dynamic page:', {
      slug: context.params?.slug,
      lang: context.params?.lang,
      error: error instanceof Error ? error.message : error,
    })

    return {
      notFound: true,
    }
  }
}

// Add SSR translation support
export const getStaticProps: GetStaticProps = withLocalizedSettings(
  async (context, localizedData) => {
    const result = await getStaticPropsOriginal(context, localizedData)
    if ('props' in result) {
      const translatedProps = await addTranslations(result.props, context)
      return {
        props: translatedProps,
      }
    }

    return result
  }
)

export const getStaticPaths: GetStaticPaths = async () => {
  const limiter =
    process.env.VERCEL_GIT_COMMIT_REF === 'main' ? '[0..10]' : '[0..10]'

  const pages = await sanityClientCdn.fetch<
    Array<{ url: PageType; language?: string; slug: { current: string } }>
  >(
    `*[(_type in $pageTypes && _type != 'artist' && defined(slug.current) && (!defined(isVisible) || isVisible == true) && !(_id in path('drafts.**')) && slug.current != '/' && slug.current != 'homepage' && slug.current != 'index')]
      | order(_score desc)
        ${limiter} {
        ${urlQuery},
        language,
        slug
      }`,
    {
      pageTypes: ['page'],
    }
  )

  const paths = (pages?.length ? pages : [])
    .filter(p => {
      return p.url.slug
    })
    .flatMap(p => {
      // Only generate paths for the language this page is actually written in
      // English pages should be available in all locales (as fallback)
      // Non-English pages should only generate their specific language path
      const pageLanguage = p.language || 'en'

      if (pageLanguage === 'en') {
        // English pages are available in all locales
        return supportedLocales.map(lang => ({
          params: {
            lang,
            slug: p.url.slug,
          },
        }))
      } else {
        // Non-English pages only generate path for their specific language
        return [
          {
            params: {
              lang: pageLanguage,
              slug: p.url.slug,
            },
          },
        ]
      }
    })

  return {
    paths,
    fallback: 'blocking',
  }
}

export default DynamicPage
